import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useIsMobile } from "../../hooks/useResponsive";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { formatCurrencyUY } from "../../utils/formatters";
import {
  LuFileText,
  LuLoader,
  LuTriangleAlert,
  LuChevronDown,
  LuChevronUp,
  LuPrinter,
  LuReceipt,
  LuCalendarDays,
} from "react-icons/lu";

const RecibosEmployee = ({ userid }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (selectedBuilding) {
      loadPayslips();
    } else {
      setLoading(false);
    }
  }, [selectedBuilding]);

  const loadPayslips = async () => {
    setLoading(true);
    try {
      const buildingId = Number(selectedBuilding.buildingid);
      const q = query(
        collection(db, "payslips"),
        where("buildingid", "==", buildingId),
        where("employeeid", "==", String(userid)),
        orderBy("period", "desc")
      );
      const snap = await getDocs(q);
      const data = [];
      snap.forEach((d) => data.push({ id: d.id, ...d.data() }));
      setPayslips(data);
    } catch (error) {
      console.error("Error loading payslips:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (period) => {
    if (!period) return "—";
    // Expected format: "YYYY-MM" or similar
    const parts = String(period).split("-");
    if (parts.length >= 2) {
      const months = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
      ];
      const monthIdx = parseInt(parts[1], 10) - 1;
      return `${months[monthIdx] || parts[1]} ${parts[0]}`;
    }
    return period;
  };

  const getStatusBadge = (status) => {
    if (status === "emitido") {
      return { bg: "#d1fae5", color: "#065f46", label: "Emitido" };
    }
    return { bg: "#fef3c7", color: "#92400e", label: "Borrador" };
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Liquidaciones</h1>
          <p style={styles.pageSubtitle}>{selectedBuilding.name}</p>
        </div>
        {payslips.length > 0 && (
          <button style={styles.printBtn} onClick={handlePrint}>
            <LuPrinter size={14} />
            Imprimir
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div style={styles.loadingCenter}>
          <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
        </div>
      ) : payslips.length === 0 ? (
        <div style={styles.emptyState}>
          <LuFileText size={48} color="#d1d5db" />
          <h3 style={{ color: "#6b7280", fontWeight: 400, margin: "16px 0 4px" }}>
            Sin liquidaciones
          </h3>
          <p style={{ color: "#6b7280", fontSize: 14 }}>
            No hay liquidaciones disponibles todavía.
          </p>
        </div>
      ) : (
        <div style={styles.list}>
          {payslips.map((slip) => {
            const badge = getStatusBadge(slip.status);
            const isExpanded = expandedId === slip.id;
            const deductionsTotal = (slip.deductions || []).reduce(
              (sum, d) => sum + (d.amount || 0), 0
            );
            const additionsTotal = (slip.additions || []).reduce(
              (sum, a) => sum + (a.amount || 0), 0
            );

            return (
              <div key={slip.id} style={styles.card}>
                <div
                  style={styles.cardMain}
                  onClick={() => toggleExpand(slip.id)}
                >
                  <div style={styles.cardLeft}>
                    <div style={styles.periodIcon}>
                      <LuCalendarDays size={18} color="#0080ff" />
                    </div>
                    <div>
                      <h3 style={styles.periodTitle}>{formatPeriod(slip.period)}</h3>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: badge.bg,
                        color: badge.color,
                      }}>
                        {badge.label}
                      </span>
                    </div>
                  </div>
                  <div style={styles.cardRight}>
                    <div style={styles.amounts}>
                      <div style={styles.amountRow}>
                        <span style={styles.amountLabel}>Bruto</span>
                        <span style={styles.amountValue}>{formatCurrencyUY(slip.grossPay)}</span>
                      </div>
                      <div style={styles.amountRow}>
                        <span style={styles.amountLabel}>Deducciones</span>
                        <span style={{ ...styles.amountValue, color: "#ef4444" }}>
                          -{formatCurrencyUY(deductionsTotal)}
                        </span>
                      </div>
                      <div style={styles.amountRow}>
                        <span style={{ ...styles.amountLabel, fontWeight: 400 }}>Neto</span>
                        <span style={{ ...styles.amountValue, fontWeight: 400, color: "#059669", fontSize: 18 }}>
                          {formatCurrencyUY(slip.netPay)}
                        </span>
                      </div>
                    </div>
                    <div style={styles.expandIcon}>
                      {isExpanded ? <LuChevronUp size={14} color="#6b7280" /> : <LuChevronDown size={14} color="#6b7280" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={styles.expandedContent}>
                    <div style={styles.divider} />

                    {/* Work info */}
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Horas trabajadas</span>
                      <span style={styles.detailValue}>{slip.hoursWorked || "—"}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Días trabajados</span>
                      <span style={styles.detailValue}>{slip.daysWorked || "—"}</span>
                    </div>

                    {/* Additions */}
                    {(slip.additions || []).length > 0 && (
                      <>
                        <h4 style={styles.sectionLabel}>Haberes</h4>
                        {slip.additions.map((item, idx) => (
                          <div key={idx} style={styles.detailRow}>
                            <span style={styles.detailLabel}>{item.concept || "—"}</span>
                            <span style={{ ...styles.detailValue, color: "#059669" }}>
                              +{formatCurrencyUY(item.amount)}
                            </span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Deductions */}
                    {(slip.deductions || []).length > 0 && (
                      <>
                        <h4 style={styles.sectionLabel}>Deducciones</h4>
                        {slip.deductions.map((item, idx) => (
                          <div key={idx} style={styles.detailRow}>
                            <span style={styles.detailLabel}>{item.concept || "—"}</span>
                            <span style={{ ...styles.detailValue, color: "#ef4444" }}>
                              -{formatCurrencyUY(item.amount)}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 900,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 0",
  },
  printBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    backgroundColor: "#ffffff",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: 48,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 48,
    textAlign: "center",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  cardMain: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    cursor: "pointer",
  },
  cardLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  periodIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: "#f0f7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  periodTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#111827",
    margin: "0 0 4px",
  },
  statusBadge: {
    fontSize: 11,
    fontWeight: 400,
    padding: "2px 8px",
    borderRadius: 4,
  },
  cardRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  amounts: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    textAlign: "right",
  },
  amountRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
  },
  amountLabel: {
    fontSize: 12,
    color: "#6b7280",
  },
  amountValue: {
    fontSize: 14,
    fontWeight: 400,
    color: "#374151",
  },
  expandIcon: {
    padding: 4,
  },
  expandedContent: {
    padding: "0 20px 20px",
  },
  divider: {
    height: 1,
    backgroundColor: "#e5e7eb",
    marginBottom: 16,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
  },
  detailLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  detailValue: {
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    margin: "16px 0 8px",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

export default RecibosEmployee;
